import type { OrganisationSeatPlan } from '@dreamai/types';
import { formatCredits, formatMinor } from './organisationCreditLabels';

/**
 * Plans are stored as whole-number multiples of the seat price (the
 * subscription quantity), but admins only ever see the resulting monthly
 * price per member and the credits it buys. These are the multiples offered
 * as ready-made choices; anything else is "custom".
 */
export const SUGGESTED_PLAN_MULTIPLIERS = [3, 8, 20] as const;

export const MAX_PLAN_MULTIPLIER = 200;

/**
 * Bigger plans earn a bonus on top of the seat product's flat credits per
 * unit:
 *
 *   credits = base × units × min(MAX_PLAN_BONUS_FACTOR, 1 + units × PLAN_BONUS_PER_UNIT)
 *
 * e.g. base 1,500 and 5 units → 1,500 × 5 × 1.05 = 7,875. Mirrors
 * dream-be lib/organisations/planCredits.ts; keep the two in step.
 */
export const PLAN_BONUS_PER_UNIT = 0.01;
export const MAX_PLAN_BONUS_FACTOR = 1.5;

export function planBonusFactor(units: number): number {
  return Math.min(MAX_PLAN_BONUS_FACTOR, 1 + units * PLAN_BONUS_PER_UNIT);
}

/** Credits one member on `units` plan units receives per month. */
export function planCredits(
  units: number,
  baseCreditsPerUnit: number | null,
): number {
  if (!baseCreditsPerUnit || units <= 0) return 0;
  return Math.round(baseCreditsPerUnit * units * planBonusFactor(units));
}

/** Whole-percent bonus over the flat rate, e.g. 5 for a 5-unit plan. */
export function planBonusPercent(units: number): number {
  return Math.round((planBonusFactor(units) - 1) * 100);
}

export interface PlanSummary {
  /** e.g. "£25" — per member per month */
  price: string;
  /** e.g. "4,000" — credits per member per month */
  credits: string;
  /** e.g. 5 — percent more credits than the flat rate would give */
  bonusPercent: number;
}

export function describePlan(
  multiplier: number,
  seatPlan: OrganisationSeatPlan,
): PlanSummary {
  return {
    price: formatMinor(
      multiplier * seatPlan.unitAmountMinor,
      seatPlan.currency,
    ),
    credits: formatCredits(planCredits(multiplier, seatPlan.creditsPerUnit)),
    bonusPercent: planBonusPercent(multiplier),
  };
}

/** One line an admin can read: "£25/month per member · 4,000 credits a month". */
export function planSentence(
  multiplier: number,
  seatPlan: OrganisationSeatPlan | null,
): string {
  if (!seatPlan) return 'Plan pricing unavailable';
  const { price, credits } = describePlan(multiplier, seatPlan);
  return `${price}/month per member · ${credits} credits a month`;
}

export function isSuggestedMultiplier(multiplier: number): boolean {
  return (SUGGESTED_PLAN_MULTIPLIERS as readonly number[]).includes(multiplier);
}
